<template>
  <WizardDialog
    :visible="visible"
    :phase="wizard.phase.value"
    :phases="PHASES"
    :titles="titles"
    :subtitles="subtitles"
    :locked="locked"
    :full-screen="true"
    :close-label="t('billing.change.close')"
    @update:visible="onVisibleChange"
    @close="onClose"
  >
    <!-- Phase 1 : Plan selection -->
    <template #select>
      <div class="change-plan__phase-body">
        <!-- Support state: V4 shop without page views computed yet. The back
             returns `code: 'PAGES_VIEWED_UNAVAILABLE'`, PlanComparator emits
             `error` and we replace the wizard content with a help block. -->
        <div
          v-if="supportErrorCode"
          class="change-plan__support"
        >
          <div class="change-plan__support-icon">
            <i class="fa-regular fa-circle-info" />
          </div>
          <h3 class="change-plan__support-title">
            {{ t('billing.change.support.title') }}
          </h3>
          <p class="change-plan__support-body">
            {{ t('billing.change.support.body') }}
          </p>
          <Button
            class="change-plan__support-cta p-button"
            icon="fa-regular fa-message"
            :label="t('billing.change.support.cta')"
            @click="openSupport"
          />
        </div>
        <PlanComparator
          v-else
          v-model="selectedPlan"
          v-model:frequency="selectedFrequency"
          v-model:targetTier="selectedTier"
          v-model:tier-auto-upgrade-enabled="tierAutoUpgradeEnabled"
          v-model:tier-auto-upgrade-max="tierAutoUpgradeMax"
          :current-plan-code="initialPlan"
          :discount-percent="discountPercent"
          @confirm="wizard.next"
          @error="onPlanComparatorError"
        />
      </div>
    </template>

    <!-- Phase 2 : Credits (optional inclusion) -->
    <template #credits>
      <div class="change-plan__phase-body">
        <AddCreditsStep
          v-model="selectedSmsInclusion"
          :currency="currency"
          :target-plan="selectedPlan"
          :initial-sms-inclusion="currentSmsPlanAmount"
        />
      </div>
      <div class="change-plan__foot change-plan__foot--split">
        <WizardBackButton
          :label="t('billing.change.back')"
          @click="wizard.back"
        />
        <WizardActionButton
          variant="next"
          :label="t('billing.change.continue')"
          trailing-icon="fa-regular fa-arrow-right"
          @click="wizard.next"
        />
      </div>
    </template>

    <!-- Phase 3 : Review + Payment -->
    <template #review>
      <div class="change-plan__phase-body">
        <ReviewAndPayStep
          v-if="selectedPlan"
          :plan="selectedPlan"
          :frequency="selectedFrequency"
          :target-tier="selectedTier"
          :sms-inclusion="selectedSmsInclusion"
          :current-plan="initialPlan"
          :tier-auto-upgrade-enabled="tierAutoUpgradeEnabled"
          :tier-auto-upgrade-max="tierAutoUpgradeMax"
          @paid="onPaid"
          @shopify-redirect="onShopifyRedirect"
        />
      </div>
      <div
        v-if="!locked"
        class="change-plan__foot"
      >
        <WizardBackButton
          :label="t('billing.change.back')"
          @click="wizard.back"
        />
      </div>
    </template>
  </WizardDialog>
</template>

<script lang="ts">
import {
  computed, defineComponent, watch,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import Button from 'primevue/button';
import Gleap from 'gleap';
import WizardDialog from '@/components/billing/shared/WizardDialog.vue';
import WizardBackButton from '@/components/billing/shared/WizardBackButton.vue';
import WizardActionButton from '@/components/billing/shared/WizardActionButton.vue';
import PlanComparator from '@/components/billing/dialogs/PlanComparator.vue';
import AddCreditsStep from '@/components/billing/dialogs/AddCreditsStep.vue';
import ReviewAndPayStep from '@/components/billing/dialogs/ReviewAndPayStep.vue';
import { useWizard } from '@/composables/billing/useWizard';

const PHASES = ['select', 'credits', 'review'] as const;

export default defineComponent({
  name: 'ChangePlanDialog',

  components: {
    Button,
    WizardDialog,
    WizardBackButton,
    WizardActionButton,
    PlanComparator,
    AddCreditsStep,
    ReviewAndPayStep,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    initialPlan: {
      type: String as () => string | null,
      default: null,
    },

    initialFrequency: {
      type: String as () => 'monthly' | 'yearly',
      default: 'monthly',
    },

    /**
     * Shop-level active discount percentage on the plan price (forwarded
     * to PlanComparator). When set, each plan card displays both the
     * struck-through catalog price AND the discounted price + a "-X%"
     * pill. Null / 0 ⇒ no discount UI on the comparator.
     */
    discountPercent: {
      type: Number as () => number | null,
      default: null,
    },
  },

  emits: ['update:visible', 'paid'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const wizard = useWizard(PHASES);

    const selectedPlan = ref<string | null>(props.initialPlan);
    const selectedFrequency = ref<'monthly' | 'yearly'>(props.initialFrequency);
    const selectedTier = ref<number | undefined>(undefined);
    const selectedSmsInclusion = ref<number>(0);
    const locked = ref(false);
    // Error code emitted by PlanComparator when the back returns a structured
    // 400 (e.g. PAGES_VIEWED_UNAVAILABLE for a V4 shop without page stats).
    // When set, the wizard replaces phase 1 with a "Contact support" block.
    const supportErrorCode = ref<string | null>(null);

    // V5 auto-tier-upgrade configuration. Active par défaut, plafond auto-
    // calculé par TierAutoUpgradeBlock (= tier choisi + 3 paliers, ou tier
    // max disponible). Forwardé à `subscribePlan` via ReviewAndPayStep.
    const tierAutoUpgradeEnabled = ref<boolean>(true);
    const tierAutoUpgradeMax = ref<number>(0);

    const onPlanComparatorError = (code: string) => {
      supportErrorCode.value = code;
    };

    const openSupport = () => {
      try {
        Gleap.open();
      } catch {
        // Gleap not initialised — fallback to a direct mailto.
        window.open(
          'mailto:contact@shopimind.com?subject=Demande%20de%20support%20-%20Changement%20d%27offre',
          '_blank',
        );
      }
    };

    const currency = computed<'EUR' | 'USD'>(
      () => (store.state.billing.overview?.shop?.currency === 'USD' ? 'USD' : 'EUR'),
    );

    // Pre-fill source for `AddCreditsStep` — the shop's currently subscribed
    // monthly SMS amount. Read from the overview's `plan.currentSmsPlan` (set
    // by the back from `shops_plans.plan.smsPlan`). If null, the toggle opens
    // deactivated; if > 0, the toggle is on and the amount is pre-saisi.
    const currentSmsPlanAmount = computed<number>(() => (
      store.state.billing.overview?.plan?.currentSmsPlan?.amount ?? 0
    ));

    const titles = computed(() => ({
      select: t('billing.change.titleSelect'),
      credits: t('billing.change.titleCredits'),
      review: t('billing.change.titleReview'),
    }));

    const subtitles = computed(() => ({
      select: t('billing.change.subtitleSelect'),
      credits: t('billing.change.subtitleCredits'),
      review: t('billing.change.subtitleReview'),
    }));

    const reset = () => {
      wizard.reset();
      selectedPlan.value = props.initialPlan;
      selectedFrequency.value = props.initialFrequency;
      selectedTier.value = undefined;
      selectedSmsInclusion.value = 0;
      locked.value = false;
      supportErrorCode.value = null;
      tierAutoUpgradeEnabled.value = true;
      tierAutoUpgradeMax.value = 0;
    };

    const onVisibleChange = (v: boolean) => {
      if (!v) onClose();
    };

    const onClose = () => {
      if (locked.value) return;
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    const onPaid = () => {
      // For upgrades, `handleReload` (inside ReviewAndPayStep) reloads the
      // page on `requiresAuthRefresh=true` — the modal disappears as a
      // side-effect of the navigation. Downgrades return `completed` WITHOUT
      // `requiresAuthRefresh` (no payment was made) — without an explicit
      // close here the modal would linger on top of the freshly-refreshed
      // overview.
      locked.value = true;
      emit('paid');
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    const onShopifyRedirect = () => {
      locked.value = true;
    };

    watch(() => props.visible, (v) => { if (v) reset(); });

    return {
      t,
      PHASES,
      wizard,
      titles,
      subtitles,
      selectedPlan,
      selectedFrequency,
      selectedTier,
      selectedSmsInclusion,
      currency,
      currentSmsPlanAmount,
      tierAutoUpgradeEnabled,
      tierAutoUpgradeMax,
      locked,
      supportErrorCode,
      onPlanComparatorError,
      openSupport,
      onVisibleChange,
      onClose,
      onPaid,
      onShopifyRedirect,
    };
  },
});
</script>

<style lang="scss" scoped>
.change-plan {
  // The phase body scrolls (only when needed), the foot stays pinned at the
  // bottom of the dialog. The wizard chrome (in fullScreen mode) gives us
  // a viewport-tall phase, so the body always has room to flex/scroll and
  // the foot can never disappear off-screen.
  &__phase-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    // Horizontal padding keeps the cards / topbar away from the scrollbar
    // (otherwise the Pro panel butts right against the gutter).
    padding: 0 16px 16px;
  }

  &__foot {
    display: flex;
    align-items: center;
    padding: 16px 0;
    background: #fff;
    flex-shrink: 0;
    border-top: 1px solid #f1f5f9;

    &--split {
      justify-content: space-between;
      gap: 12px;
    }
  }

  // Support state — shown when the back returns a structured error
  // (e.g. `PAGES_VIEWED_UNAVAILABLE`). Centered card with a Gleap CTA.
  &__support {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 48px 24px;
    gap: 14px;
    max-width: 520px;
    margin: 0 auto;
  }

  &__support-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: #f1f5f9;
    color: #475569;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
  }

  &__support-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #0f172a;
    margin: 4px 0 0;
  }

  &__support-body {
    font-size: 0.875rem;
    color: #475569;
    line-height: 1.55;
    margin: 0;
  }

  &__support-cta {
    margin-top: 8px;
  }
}
</style>
